import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from './button'

/**
 * Modal — fokus tuzog'i, Esc, overlay bosish.
 *
 * SKILL §7 (xavfli amallar) uchun muhim xatti-harakat:
 *   · `initialFocus` — DangerDialog fokusni «Bekor qilish»ga qo'yadi
 *   · Enter buzuvchi amalni BAJARMAYDI (bu yerda form submit yo'q)
 *   · Esc — doim bekor qiladi
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  icon,
  children,
  footer,
  width = 'md',
  initialFocusRef,
  closeOnOverlay = true,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg'
  initialFocusRef?: React.RefObject<HTMLElement>
  closeOnOverlay?: boolean
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!open) return

    restoreRef.current = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTarget = initialFocusRef?.current ?? panelRef.current
    // Bo'yashdan keyin fokus — element hali DOM'da bo'lmasligi mumkin.
    const raf = requestAnimationFrame(() => focusTarget?.focus())

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return

      // Fokus tuzog'i: Tab modal ichida aylanadi.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = prevOverflow
      ;(restoreRef.current as HTMLElement | null)?.focus?.()
    }
  }, [open, onClose, initialFocusRef])

  if (!open) return null

  const widths = { sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[760px]' }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-3 sm:p-6"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cn(
          'w-full my-auto rounded-lg border border-border-default bg-surface-2 outline-none',
          widths[width],
        )}
        style={{ boxShadow: 'var(--shadow-panel)' }}
      >
        <header className="flex items-start gap-3 border-b border-border-subtle px-4 py-3">
          {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
          <div className="min-w-0 flex-1">
            <h2 className="text-md font-semibold text-primary">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-secondary">{description}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="close" className="-mr-1 -mt-1">
            <X size={15} />
          </Button>
        </header>

        {children && <div className="px-4 py-3">{children}</div>}

        {footer && (
          <footer className="flex items-center gap-2 border-t border-border-subtle px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}
